import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import LoggingFlow from '../components/LoggingFlow';

const C = {
  bg: '#0D0D12', surface: '#16161F', card: '#1C1C28', border: '#2A2A3A',
  accent: '#E8A2B8', accentSoft: '#C97A9A', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5', purple: '#B39DDB',
};

const cyclePhases = [
  { label: 'Menstrual', day: 'Days 1–5', color: '#E8A2B8' },
  { label: 'Follicular', day: 'Days 6–13', color: '#D4AF7A' },
  { label: 'Ovulation', day: 'Day 14', color: '#7ECECA' },
  { label: 'Luteal', day: 'Days 15–28', color: '#7ECBA5' },
];

function getPhaseColor(d) {
  if (d <= 5) return '#E8A2B8';
  if (d <= 13) return '#D4AF7A';
  if (d === 14) return '#7ECECA';
  return '#7ECBA5';
}

function getPhaseLabel(d) {
  if (d <= 5) return 'MENSTRUAL';
  if (d <= 13) return 'FOLLICULAR';
  if (d === 14) return 'OVULATION';
  return 'LUTEAL';
}

function RadialCycleChart({ currentDay = 14 }) {
  const radius = 80; const cx = 100; const cy = 100;
  const currentAngle = (currentDay / 28) * 360 - 90;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const px = cx + radius * Math.cos(toRad(currentAngle));
  const py = cy + radius * Math.sin(toRad(currentAngle));
  const phaseAngles = [
    { start: -90, end: -90 + (5 / 28) * 360, color: '#E8A2B8' },
    { start: -90 + (5 / 28) * 360, end: -90 + (13 / 28) * 360, color: '#D4AF7A' },
    { start: -90 + (13 / 28) * 360, end: -90 + (14 / 28) * 360, color: '#7ECECA' },
    { start: -90 + (14 / 28) * 360, end: 270, color: '#7ECBA5' },
  ];
  const arcPath = (s0, e0, r, ir) => {
    const s = toRad(s0); const e = toRad(e0);
    const x1 = cx + r * Math.cos(s); const y1 = cy + r * Math.sin(s);
    const x2 = cx + r * Math.cos(e); const y2 = cy + r * Math.sin(e);
    const ix1 = cx + ir * Math.cos(s); const iy1 = cy + ir * Math.sin(s);
    const ix2 = cx + ir * Math.cos(e); const iy2 = cy + ir * Math.sin(e);
    const lg = e0 - s0 > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${lg} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${ir} ${ir} 0 ${lg} 0 ${ix1} ${iy1} Z`;
  };
  return (
    <svg width="200" height="200" viewBox="0 0 200 200">
      {phaseAngles.map((p, i) => <path key={i} d={arcPath(p.start, p.end, 90, 62)} fill={p.color} opacity="0.85" />)}
      <circle cx={cx} cy={cy} r={58} fill={C.card} />
      <text x={cx} y={cy - 10} textAnchor="middle" fill={C.text} fontSize="22" fontWeight="700" fontFamily="'DM Serif Display',serif">Day {currentDay}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill={C.muted} fontSize="9" fontFamily="monospace">{getPhaseLabel(currentDay)}</text>
      <text x={cx} y={cy + 26} textAnchor="middle" fill={getPhaseColor(currentDay)} fontSize="8" fontFamily="monospace">NEXT PERIOD IN {28 - currentDay}d</text>
      <circle cx={px} cy={py} r={5} fill={C.text} />
      <circle cx={px} cy={py} r={8} fill="none" stroke={C.text} strokeWidth="1.5" opacity="0.4" />
    </svg>
  );
}

function calcDayOfCycle(cycles) {
  if (!cycles || cycles.length === 0) return null;
  const start = new Date(cycles[0].start_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
  return Math.min(Math.max(diff, 1), 28);
}

export default function Home({ user, setActiveTab }) {
  const [dayOfCycle, setDayOfCycle] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [logKey, setLogKey] = useState(0); // bump to reset LoggingFlow after save

  const emailName = user?.email?.split('@')[0] || 'there';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    // Fetch cycle info
    axios.get(`${API_URL}/api/cycles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDayOfCycle(calcDayOfCycle(r.data)))
      .catch(() => {});
    // Fetch recent sessions for pattern memory
    axios.get(`${API_URL}/api/sessions?limit=30`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRecentSessions(r.data || []))
      .catch(() => {});
  }, [logKey]);

  const handleSaved = () => {
    // Refresh sessions after save so pattern memory updates
    setTimeout(() => setLogKey(k => k + 1), 800);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Greeting */}
      <div>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', letterSpacing: '0.08em' }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase()}
        </div>
        <h1 style={{ fontSize: 24, color: C.text, fontFamily: "'DM Serif Display',serif", margin: '4px 0 2px' }}>
          Hello, {emailName}
        </h1>
        <p style={{ fontSize: 13, color: C.accentSoft, margin: 0 }}>
          {dayOfCycle
            ? `You're in your ${getPhaseLabel(dayOfCycle).toLowerCase()} phase.`
            : 'Log your period start to track your cycle.'}
        </p>
      </div>

      {/* Cycle ring */}
      <div style={{ background: C.card, borderRadius: 20, padding: 20, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 20 }}>
        <RadialCycleChart currentDay={dayOfCycle ?? 1} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 8 }}>PHASE GUIDE</div>
          {cyclePhases.map(p => (
            <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: C.text }}>{p.label}</span>
              <span style={{ fontSize: 10, color: C.muted, marginLeft: 'auto', fontFamily: 'monospace' }}>{p.day}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Logging flow */}
      <div style={{ background: C.card, borderRadius: 18, padding: 16, border: `1px solid ${C.border}` }}>
        <LoggingFlow
          key={logKey}
          user={user}
          dayOfCycle={dayOfCycle}
          recentSessions={recentSessions}
          onSaved={handleSaved}
        />
      </div>

      {/* GP Export */}
      <div style={{ background: `linear-gradient(135deg,#1A1A28,#1C1C2A)`, borderRadius: 16, padding: 16, border: `1px solid ${C.green}44` }}>
        <div style={{ fontSize: 10, color: C.green, fontFamily: 'monospace', marginBottom: 6 }}>◎ EXPORT</div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 4 }}>Share your health report</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Generate a structured summary to share with your GP.</div>
        <button onClick={async () => {
          const token = localStorage.getItem('authToken');
          const res = await axios.get(`${API_URL}/api/export/gp`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
          const url = window.URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url; a.download = `sylva-health-report-${new Date().toISOString().split('T')[0]}.html`; a.click();
        }} style={{ background: C.green, color: '#0D0D12', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          Download report
        </button>
      </div>

      {/* Pattern nudge */}
      <div onClick={() => setActiveTab('patterns')} style={{ background: `linear-gradient(135deg,#1C1C2A,#1A1A28)`, borderRadius: 16, padding: 16, border: `1px solid ${C.purple}44`, cursor: 'pointer' }}>
        <div style={{ fontSize: 10, color: C.purple, fontFamily: 'monospace', marginBottom: 6 }}>◈ PATTERNS</div>
        <div style={{ fontSize: 14, color: C.text, fontWeight: 600, marginBottom: 4 }}>See your health patterns</div>
        <div style={{ fontSize: 12, color: C.muted }}>Log consistently to unlock personalised insights across your cycles.</div>
      </div>

    </div>
  );
}
